import React, { useState, useCallback, useEffect } from 'react';
import { Video, Mic, MicOff, VideoOff, Users } from 'lucide-react';

interface PreJoinModalProps {
    isOpen: boolean;
    onJoin: (options: { camera: boolean; microphone: boolean }) => void;
    participantName?: string;
}

const STORAGE_KEY_CAMERA = 'focusmode_camera_enabled';
const STORAGE_KEY_MIC = 'focusmode_mic_enabled';

const getStoredPreference = (key: string): boolean => {
    try {
        const stored = localStorage.getItem(key);
        return stored ? JSON.parse(stored) : false;
    } catch {
        return false;
    }
};

const PreJoinModal: React.FC<PreJoinModalProps> = ({ isOpen, onJoin, participantName }) => {
    const [cameraEnabled, setCameraEnabled] = useState(() => getStoredPreference(STORAGE_KEY_CAMERA));
    const [microphoneEnabled, setMicrophoneEnabled] = useState(() => getStoredPreference(STORAGE_KEY_MIC));
    const [isChecking, setIsChecking] = useState(false);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY_CAMERA, JSON.stringify(cameraEnabled));
    }, [cameraEnabled]);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY_MIC, JSON.stringify(microphoneEnabled));
    }, [microphoneEnabled]);

    const checkPermission = useCallback(async (type: 'camera' | 'microphone') => {
        if (isChecking) return;
        setIsChecking(true);
        try {
            const constraints: MediaStreamConstraints = type === 'camera'
                ? { video: true }
                : { audio: true };
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            // Immediately stop the test stream
            stream.getTracks().forEach(track => track.stop());
            if (type === 'camera') {
                setCameraEnabled(true);
            } else {
                setMicrophoneEnabled(true);
            }
        } catch (error) {
            console.warn(`Permission denied for ${type}:`, error);
            // Reset the toggle if permission is denied
            if (type === 'camera') {
                setCameraEnabled(false);
            } else {
                setMicrophoneEnabled(false);
            }
        } finally {
            setIsChecking(false);
        }
    }, [isChecking]);

    const toggleCamera = useCallback(() => {
        if (!cameraEnabled) {
            checkPermission('camera');
        } else {
            setCameraEnabled(false);
        }
    }, [cameraEnabled, checkPermission]);

    const toggleMicrophone = useCallback(() => {
        if (!microphoneEnabled) {
            checkPermission('microphone');
        } else {
            setMicrophoneEnabled(false);
        }
    }, [microphoneEnabled, checkPermission]);

    const handleJoin = () => {
        onJoin({ camera: cameraEnabled, microphone: microphoneEnabled });
    };

    const backToDashboard = () => {

        if (window.history.length > 1) {
            window.history.back();
        } else {
            window.location.href = "/dashboard";
        }
    }

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl">
                {/* Header */}
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-orange-600" />
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900">Ready to join?</h3>
                </div>

                {participantName && (
                    <p className="text-gray-500 mb-6 ml-[3.25rem]">Joining as <span className="text-gray-700 font-medium">{participantName}</span></p>
                )}

                {/* Permissions Section */}
                <div className="space-y-4 my-6">
                    <p className="text-sm font-medium text-gray-700">Choose your preferences:</p>

                    {/* Camera */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${cameraEnabled
                                ? 'bg-green-100'
                                : 'bg-gray-200'
                                }`}>
                                {cameraEnabled ? (
                                    <Video className="w-5 h-5 text-green-600" />
                                ) : (
                                    <VideoOff className="w-5 h-5 text-gray-500" />
                                )}
                            </div>
                            <div>
                                <p className="font-medium text-gray-900">Camera</p>
                                <p className="text-xs text-gray-500">
                                    {cameraEnabled ? 'Your camera is on' : 'Turn on your camera'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={toggleCamera}
                            disabled={isChecking}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${cameraEnabled
                                ? 'bg-green-500'
                                : 'bg-gray-300'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${cameraEnabled
                                    ? 'translate-x-6'
                                    : 'translate-x-1'
                                    }`}
                            />
                        </button>
                    </div>

                    {/* Microphone */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${microphoneEnabled
                                ? 'bg-green-100'
                                : 'bg-gray-200'
                                }`}>
                                {microphoneEnabled ? (
                                    <Mic className="w-5 h-5 text-green-600" />
                                ) : (
                                    <MicOff className="w-5 h-5 text-gray-500" />
                                )}
                            </div>
                            <div>
                                <p className="font-medium text-gray-900">Microphone</p>
                                <p className="text-xs text-gray-500">
                                    {microphoneEnabled ? 'Your mic is on' : 'Turn on your microphone'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={toggleMicrophone}
                            disabled={isChecking}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${microphoneEnabled
                                ? 'bg-green-500'
                                : 'bg-gray-300'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${microphoneEnabled
                                    ? 'translate-x-6'
                                    : 'translate-x-1'
                                    }`}
                            />
                        </button>

                    </div>
                </div>

                {/* Info note */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                    <p className="text-xs text-blue-700">
                        <span className="font-medium">Note:</span> You can always enable camera and microphone after joining.
                    </p>
                </div>

                {/* Actions */}
                <div className="space-y-3">
                    <button
                        onClick={handleJoin}
                        className="w-full py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-medium transition-colors text-sm"
                    >
                        Join Session
                    </button>
                    <button
                        onClick={backToDashboard}
                        className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors text-sm"
                    >
                        Back to Dashboard
                    </button>
                </div>

            </div>
        </div >
    );
};

export default PreJoinModal;
